import React, { useEffect, useMemo } from 'react';
import { useUser } from '@reducers/user/hooks';
import Box from '@components/Atoms/Box';
import {
  Button,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ConstructionIcon from '@mui/icons-material/Construction';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ShowIf from '@components/Atoms/ShowIf';
import { toast } from 'react-toastify';
import StarsIcon from '@mui/icons-material/Stars';
import Modal from '@mui/material/Modal';
import { useCreateLessonSlide, useSaveSlide } from './mutations';
import DraftMenuItem from './DraftMenuItem';
import { getSlideIdFromUrl } from './utils';
import { CompareSlideObject, LearnhubLesson, LearnhubReview } from '../types';
import ModalGuide from './ModalGuide';
import SavedMenuItem from './SavedMenuItem';
import CompareModalContent from './CompareModalContent';
import SlideEditReminderModal from './SlideEditReminderModal';
import useSlideEditReminder from './useSlideEditReminder';

export const STATE = {
  NO_COPIES: 'NO_COPIES',
  DRAFT: 'DRAFT',
  SAVED: 'SAVED', // a saved copy exists but no draft
  SAVED_COPY: 'SAVED_COPY', // a saved copy has a draft
};

const getReviewState = (review) => {
  if (review) {
    if (review.savedSlide) {
      if (review.draftSlide) {
        return STATE.SAVED_COPY;
      }
      return STATE.SAVED;
    }

    if (review.draftSlide) {
      return STATE.DRAFT;
    }
  }

  return STATE.NO_COPIES;
};

export const SLIDES = {
  CHALKTALK: 'CHALKTALK',
  MY_SAVED_VERSION: 'MY_SAVED_VERSION',
  MY_DRAFT_VERSION: 'MY_DRAFT_VERSION',
};

interface SlideControlsProps {
  lessonData: LearnhubLesson;
  reviewData?: LearnhubReview;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  setPresentationSlide: (slideId: string | undefined) => void;
}

const SlideControls: React.FC<SlideControlsProps> = ({
  lessonData,
  reviewData,
  isLoading,
  setIsLoading,
  setPresentationSlide,
}) => {
  const user = useUser();
  const menuRef = React.useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [guideModalOpen, setGuideModalOpen] = React.useState(false);
  const [leftSlide, setLeftSlide] = React.useState<CompareSlideObject | null>(null);
  const [rightSlide, setRightSlide] = React.useState<CompareSlideObject | null>(null);
  const reviewState = useMemo(() => getReviewState(reviewData), [reviewData]);
  const [selectedSlide, setSelectedSlide] = React.useState<string>(SLIDES.CHALKTALK);

  const { mutate: copySlide, isLoading: isCopying } = useCreateLessonSlide(
    user.id,
    lessonData?.id,
    {
      onSuccess: () => {
        toast.success('Slide copy created successfully!');
        setSelectedSlide(SLIDES.MY_DRAFT_VERSION);
      },
      onError: (message) => {
        toast.error(message || 'Failed to create slide copy');
      },
    },
  );

  const { mutate: saveSlide, isLoading: isSaving } = useSaveSlide(
    user.id,
    lessonData?.id,
    {
      onSuccess: () => {
        toast.success('Slide saved successfully!');
        setSelectedSlide(SLIDES.MY_SAVED_VERSION);
      },
      onError: (message) => {
        toast.error(message || 'Failed to save slide');
      },
    },
  );

  useEffect(() => {
    setIsLoading(isCopying || isSaving);
  }, [isCopying, isSaving, setIsLoading]);

  // Handle slide edit reminders for navigation
  const slideEditReminder = useSlideEditReminder({
    reviewData,
    onSave: () => saveSlide(),
    onCompare: () => {
      // Trigger compare functionality
      const currentSlide = reviewData?.draftSlide || reviewData?.savedSlide;
      const originalSlide = getSlideIdFromUrl(lessonData?.instructor_url);

      if (currentSlide && originalSlide) {
        setLeftSlide({
          slideId: originalSlide,
          label: 'ChalkTalk Version',
        });
        setRightSlide({
          slideId: currentSlide,
          label:
            reviewState === STATE.DRAFT ? 'My Version (Draft)' : 'My Version (Saved)',
        });
      }
    },
    selectedSlide,
    setSelectedSlide,
  });

  const openSlide = (slideId?: string) => {
    setMenuOpen(false);
    if (!slideId) {
      return;
    }
    window.open(`https://docs.google.com/presentation/d/${slideId}/edit`, '_blank');
  };

  useEffect(() => {
    switch (selectedSlide) {
      case SLIDES.CHALKTALK:
        setPresentationSlide(getSlideIdFromUrl(lessonData?.instructor_url));
        break;
      case SLIDES.MY_DRAFT_VERSION:
        setPresentationSlide(reviewData?.draftSlide);
        break;
      default:
        setPresentationSlide(reviewData?.savedSlide);
    }
  }, [lessonData, reviewData, selectedSlide, setPresentationSlide]);

  useEffect(() => {
    if (reviewState === STATE.NO_COPIES) {
      setSelectedSlide(SLIDES.CHALKTALK);
    } else if (reviewState === STATE.SAVED) {
      setSelectedSlide(SLIDES.MY_SAVED_VERSION);
    } else if (reviewState === STATE.DRAFT || reviewState === STATE.SAVED_COPY) {
      setSelectedSlide(SLIDES.MY_DRAFT_VERSION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ModalGuide modalOpen={guideModalOpen} setModalOpen={setGuideModalOpen} />
      <Modal
        open={Boolean(leftSlide && rightSlide)}
        aria-labelledby="compare-modal-title"
        aria-describedby="compare-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '65% ',
            height: '85%',
          }}
        >
          <CompareModalContent
            leftSlide={leftSlide}
            rightSlide={rightSlide}
            onClose={() => {
              setLeftSlide(null);
              setRightSlide(null);
            }}
          />
        </Box>
      </Modal>
      <SlideEditReminderModal
        open={slideEditReminder.showModal}
        reviewState={slideEditReminder.reviewState}
        onCompare={slideEditReminder.handleCompare}
        onSave={slideEditReminder.handleSave}
        onSkip={slideEditReminder.handleSkip}
        onTeachCurrent={slideEditReminder.handleTeachCurrent}
        onTeachNew={slideEditReminder.handleTeachNew}
        onCancel={slideEditReminder.handleCancel}
      />
      <Button
        id="slide-controls-button"
        datatest-id="slide-controls-button"
        disabled={isLoading}
        aria-controls={menuOpen ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={() => setMenuOpen(!menuOpen)}
        variant="outlined"
        endIcon={<ExpandMoreIcon />}
        size="small"
        ref={menuRef}
        sx={{
          textTransform: 'none',
          fontSize: '0.875rem',
          color: 'text.primary',
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.light',
          },
          mr: '2rem',
          width: '17rem',
        }}
      >
        <ShowIf If={selectedSlide === SLIDES.CHALKTALK}>
          <HomeIcon sx={{ mr: 1 }} />
          ChalkTalk Version
        </ShowIf>

        <ShowIf If={selectedSlide === SLIDES.MY_DRAFT_VERSION}>
          <ConstructionIcon sx={{ mr: 1 }} />
          My Version (Draft)
        </ShowIf>

        <ShowIf If={selectedSlide === SLIDES.MY_SAVED_VERSION}>
          <StarsIcon sx={{ mr: 1 }} />
          My Version (Saved)
        </ShowIf>
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={menuRef?.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        MenuListProps={{
          'aria-labelledby': 'slide-controls-button',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '26rem',
            },
          },
        }}
      >
        {/* TITLE MENU ITEM */}
        <MenuItem
          sx={{
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <ListItemText
            primary="Learn Hub Slides"
            primaryTypographyProps={{
              sx: { textAlign: 'center', width: '100%' },
            }}
          />

          <Tooltip title="What is this?">
            <IconButton
              size="small"
              aria-label="What is this?"
              onClick={() => {
                setMenuOpen(false);
                setGuideModalOpen(true);
              }}
              sx={{
                ml: 'auto',
                pointerEvents: 'auto',
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <QuestionMarkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MenuItem>

        <Divider />

        {/* CHALKTALK SLIDE MENU ITEM */}
        <MenuItem
          onClick={() => {
            setMenuOpen(false);
            setSelectedSlide(SLIDES.CHALKTALK);
          }}
        >
          <HomeIcon sx={{ mr: 1 }} />
          <ListItemText
            primary="ChalkTalk Version"
            secondary={
              reviewState === STATE.NO_COPIES
                ? "Click 'Create a Copy' to start"
                : "Click to present ChalkTalk's version"
            }
            secondaryTypographyProps={{
              variant: 'caption',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          />
          <ShowIf If={reviewState === STATE.NO_COPIES}>
            <Box sx={{ display: 'flex', gap: '0.25rem', ml: '1rem' }}>
              <Tooltip title="Create a Copy">
                <IconButton
                  size="small"
                  onClick={() => {
                    setMenuOpen(false);
                    copySlide();
                  }}
                  aria-label="Copy"
                  sx={{
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </ShowIf>
        </MenuItem>

        {/* "SAVED" VERSION MENU */}
        <ShowIf If={reviewState === STATE.SAVED || reviewState === STATE.SAVED_COPY}>
          <SavedMenuItem
            state={reviewState}
            onMenuClick={() => {
              setMenuOpen(false);
              setSelectedSlide(SLIDES.MY_SAVED_VERSION);
            }}
            handleEdit={() => {
              setMenuOpen(false);
              copySlide();
            }}
            handleCompare={() => {
              if (!lessonData?.instructor_url || !reviewData?.savedSlide) {
                return;
              }
              setLeftSlide({
                slideId: getSlideIdFromUrl(lessonData.instructor_url),
                label: 'ChalkTalk Version',
              });
              setRightSlide({
                slideId: reviewData?.savedSlide,
                label: 'My Saved Version',
              });
            }}
          />
        </ShowIf>

        {/* "DRAFT" VERSION MENU ITEM */}
        <ShowIf If={reviewState === STATE.DRAFT || reviewState === STATE.SAVED_COPY}>
          <DraftMenuItem
            state={reviewState}
            onMenuClick={() => {
              setMenuOpen(false);
              setSelectedSlide(SLIDES.MY_DRAFT_VERSION);
            }}
            handleSave={() => {
              setMenuOpen(false);
              saveSlide();
            }}
            handleEdit={() => {
              setMenuOpen(false);
              openSlide(reviewData?.draftSlide);
            }}
            handleCompare={() => {
              if (reviewState === STATE.DRAFT && reviewData?.draftSlide) {
                setLeftSlide({
                  slideId: getSlideIdFromUrl(lessonData?.instructor_url),
                  label: 'ChalkTalk Version',
                });
                setRightSlide({
                  slideId: reviewData?.draftSlide,
                  label: 'My Draft Version',
                });
              } else if (
                reviewState === STATE.SAVED_COPY &&
                reviewData?.savedSlide &&
                reviewData?.draftSlide
              ) {
                setLeftSlide({
                  slideId: reviewData?.savedSlide,
                  label: 'My Saved Version',
                });
                setRightSlide({
                  slideId: reviewData?.draftSlide,
                  label: 'My Draft Version',
                });
              }
            }}
          />
        </ShowIf>
      </Menu>
    </>
  );
};

export default SlideControls;
